/**
 * useTrackedForm — an Admin edit form's state with corp's change tracking:
 * the form as loaded (`saved`), which fields differ from it (`changed`), the
 * "modified" pill with its revert for a field (`badge`), and Revert for all.
 *
 *   const t = useTrackedForm(() => formFrom(record));
 *   <DialogField label="Title" badge={t.badge('title', 'Title')}>…
 *   <AdminFormModal dirty={t.dirty} onRevertAll={t.revertAll} …>
 *
 * Fields compare by value (JSON), so arrays and objects count as changed only
 * when their contents differ. Pills are off on a new record (`tracking: false`),
 * where there is nothing to revert to.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { ModifiedPill } from './DialogChrome';

export interface TrackedForm<T> {
  form: T;
  saved: T;
  setForm: React.Dispatch<React.SetStateAction<T>>;
  /** Replace the loaded state too, e.g. after loading details. */
  reset: (next: T) => void;
  patch: (next: Partial<T>) => void;
  changed: Set<keyof T>;
  dirty: boolean;
  revert: (key: keyof T) => void;
  revertAll: () => void;
  /** The "modified" pill for `key`, or null while it is unchanged. */
  badge: (key: keyof T, label: string) => ReactNode;
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useTrackedForm<T extends object>(initial: () => T, { tracking = true }: { tracking?: boolean } = {}): TrackedForm<T> {
  const [form, setForm] = useState<T>(initial);
  const [saved, setSaved] = useState<T>(form);

  const changed = useMemo(
    () => new Set((Object.keys(form) as (keyof T)[]).filter((k) => !same(form[k], saved[k]))),
    [form, saved],
  );

  const patch = useCallback((next: Partial<T>) => setForm((p) => ({ ...p, ...next })), []);
  const revert = useCallback((key: keyof T) => setForm((p) => ({ ...p, [key]: saved[key] })), [saved]);
  const revertAll = useCallback(() => setForm(saved), [saved]);
  const reset = useCallback((next: T) => {
    setForm(next);
    setSaved(next);
  }, []);

  const badge = useCallback(
    (key: keyof T, label: string): ReactNode =>
      tracking && changed.has(key) ? <ModifiedPill label={label} onRevert={() => revert(key)} /> : null,
    [tracking, changed, revert],
  );

  return { form, saved, setForm, reset, patch, changed, dirty: changed.size > 0, revert, revertAll, badge };
}
